/* eslint-disable react/prop-types */
import {
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import React, { useState } from 'react'

import { batchTypesMap } from 'app/config'

import { Container } from './styles'

// Weekday index used by Batch.cadence: 0 = Mon … 6 = Sun. Mirrors formatCadence
// in helpers.js, which renders each slot as dayjs().day(weekday + 1).
const WEEKDAYS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' }
]

// cadence stores clock times as 'HH-MM' (hyphen); <input type="time"> wants a
// zero-padded 'HH:MM'. Convert both ways, tolerating non-padded stored values.
const toInputTime = (t) => {
  if (typeof t !== 'string') return ''
  const [h, m] = t.split('-')
  if (h == null || m == null) return ''
  return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`
}
const toCadenceTime = (t) =>
  typeof t === 'string' && t.includes(':') ? t.replace(':', '-') : t

/**
 * Super-Admin-only editor for an existing EXCEL batch's weekly timings.
 *
 * Gates and seeds off `currentEditingData` — the FULL, untouched batch row
 * (BaseTable passes data.data[dataIndex]). We can't use `formData` here: it is
 * built only from the form's columns, and `schedule_event` isn't one of them,
 * so it would always read as absent. We must know schedule_event exists because
 * a cadence PATCH on a batch without one 500s + rolls back (the backend's
 * cadence-resync signal dereferences batch.schedule_event.schedules).
 *
 * A Super Admin may change each existing slot's weekday and start/end time.
 * Room and slot count are preserved — every other key on the slot is carried
 * through untouched (spread first), so an unedited slot serialises
 * byte-identically and the resync signal no-ops on it. Adding/removing slots is
 * intentionally out of scope.
 *
 * On any edit we set `cadence` AND a `cadence_changed` flag on the form. The
 * flag is what api/batch.js reads to decide whether to send `cadence` at all —
 * so a course/fee/etc. edit never ships cadence, and thus never triggers the
 * (expensive, notification-heavy) future-schedule + attendance regeneration.
 */
export const BatchTimingsWidget = ({ setFieldValue, currentEditingData }) => {
  const row = currentEditingData || {}
  const cadence = row.cadence
  const editable =
    row.type === batchTypesMap.excel &&
    Boolean(row.schedule_event) &&
    Array.isArray(cadence) &&
    cadence.length > 0

  const [slots, setSlots] = useState(() =>
    Array.isArray(cadence) ? cadence.map((slot) => ({ ...slot })) : []
  )

  if (!editable) return null

  const update = (index, patch) => {
    const next = slots.map((slot, i) =>
      i === index ? { ...slot, ...patch } : slot
    )
    setSlots(next)
    setFieldValue('cadence', next)
    setFieldValue('cadence_changed', true)
  }

  return (
    <Container>
      <Typography className="title">Batch Timings — Super Admin</Typography>
      <Typography className="warning">
        Changing timings deletes and regenerates all future sessions (and their
        attendance) for enrolled students. Past sessions are unaffected. The
        room and number of weekly slots stay the same.
      </Typography>
      {slots.map((slot, index) => (
        <div className="row" key={index}>
          <FormControl variant="outlined" className="dayField">
            <Select
              value={Number(slot.weekday)}
              onChange={(e) =>
                update(index, { weekday: Number(e.target.value) })
              }
            >
              {WEEKDAYS.map((day) => (
                <MenuItem key={day.value} value={day.value}>
                  {day.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            type="time"
            variant="outlined"
            label="Start"
            InputLabelProps={{ shrink: true }}
            value={toInputTime(slot.start_time)}
            onChange={(e) =>
              update(index, { start_time: toCadenceTime(e.target.value) })
            }
          />
          <TextField
            type="time"
            variant="outlined"
            label="End"
            InputLabelProps={{ shrink: true }}
            value={toInputTime(slot.end_time)}
            onChange={(e) =>
              update(index, { end_time: toCadenceTime(e.target.value) })
            }
          />
        </div>
      ))}
    </Container>
  )
}
